import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { createRechargeRequest, validateMoneyInput, toMoney, adjustBalance } from "@/services/supabaseService";
import { supabase } from "@/lib/supabase";
import useStoreSettings from "@/hooks/useStoreSettings";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

interface PaymentMethod {
  id: string;
  name: string;
  icon: string;
  iconColor: string;
  description: string;
}

const paymentMethods: PaymentMethod[] = [
  {
    id: "bank",
    name: "銀行轉帳",
    icon: "ri-bank-line",
    iconColor: "text-amber-600",
    description: "國內外銀行電匯，安全便捷",
  },
  {
    id: "usdt",
    name: "USDT 支付",
    icon: "ri-currency-line",
    iconColor: "text-emerald-600",
    description: "支援多鏈 USDT 穩定幣轉帳",
  },
  {
    id: "store",
    name: "門市支付",
    icon: "ri-store-2-line",
    iconColor: "text-stone-600",
    description: "至指定合作門市現場繳款",
  },
  {
    id: "offline",
    name: "廠商見面收",
    icon: "ri-map-pin-line",
    iconColor: "text-orange-600",
    description: "團長親自上門收取現金",
  },
];

const steps = [
  { num: 1, label: "選擇付款方式", icon: "ri-bank-card-line" },
  { num: 2, label: "輸入儲值金額", icon: "ri-coins-line" },
  { num: 3, label: "點擊儲值申請", icon: "ri-send-plane-line" },
  { num: 4, label: "等待審核中", icon: "ri-time-line" },
];

const presetAmounts = [500, 1000, 3000, 5000, 10000];

export default function Recharge() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { lineUrl } = useStoreSettings();

  const [selectedMethod, setSelectedMethod] = useState("");
  const [amount, setAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submittedAmount, setSubmittedAmount] = useState(0);
  const [apiError, setApiError] = useState("");
  const [trialClaimed, setTrialClaimed] = useState(false);
  const [claimingTrial, setClaimingTrial] = useState(false);
  const [showTrialSuccess, setShowTrialSuccess] = useState(false);
  const [trialError, setTrialError] = useState("");

  // Check if trial bonus already claimed (by querying transactions)
  useEffect(() => {
    if (!user) return;
    async function checkIfClaimed() {
      const { data, error } = await supabase
        .from('transactions')
        .select('id')
        .eq('user_id', user.id)
        .ilike('description', '%體驗金%')
        .limit(1);
      if (!error && data && data.length > 0) {
        setTrialClaimed(true);
      }
    }
    checkIfClaimed();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 text-center py-20">
          <p className="text-gray-500 text-lg mb-4">請先登入</p>
          <button
            onClick={() => navigate("/login")}
            className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium cursor-pointer"
          >
            前往登入
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  // ============ SUBMITTED / PENDING STATE ============
  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="pt-24 pb-12 px-6">
          <div className="max-w-md mx-auto">
            {/* Back breadcrumb */}
            <div className="flex items-center gap-2 mb-8 text-sm text-gray-500">
              <button onClick={() => navigate("/dashboard")} className="hover:text-amber-600 cursor-pointer">
                個人中心
              </button>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </div>
              <span className="text-gray-700">儲值申請</span>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center space-y-6">
              {/* Animated pending icon */}
              <div className="relative w-20 h-20 mx-auto">
                <div className="absolute inset-0 rounded-full border-4 border-amber-100"></div>
                <div className="absolute inset-0 rounded-full border-4 border-amber-500 border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-amber-500 text-xl"></i>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-2">申請已提交，正在審核中</h2>
                <p className="text-sm text-gray-500 leading-relaxed">
                  您的儲值申請（${toMoney(submittedAmount).toLocaleString()}）已送出，管理員正在審核中。
                  <br />
                  請聯繫團長索取支付資訊，完成付款後將立即入帳。
                </p>
              </div>

              {/* API warning if backend failed but user can still contact leader */}
              {apiError && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-left">
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-orange-500 text-xs"></i>
                    </div>
                    <div>
                      <p className="text-xs text-orange-700 font-medium">後台同步稍有延遲</p>
                      <p className="text-xs text-orange-600 mt-0.5">{apiError}</p>
                      <p className="text-xs text-orange-500 mt-1">請直接聯繫團長完成儲值，無需擔心。</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Status timeline */}
              <div className="bg-stone-50 rounded-xl p-4 text-left space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">提交申請</p>
                    <p className="text-xs text-gray-400">剛剛</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center shrink-0 animate-pulse">
                    <i className="ri-loader-2-line text-white text-xs"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">等待管理員審核</p>
                    <p className="text-xs text-gray-400">預計 1 個工作日內完成</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                    <i className="ri-coins-line text-gray-400 text-xs"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-400">入帳完成</p>
                    <p className="text-xs text-gray-400">審核通過後自動入帳</p>
                  </div>
                </div>
              </div>

              {/* LINE CTA */}
              <a
                href={lineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-3 rounded-xl text-white font-medium text-sm hover:opacity-90 transition-all active:scale-[0.98] flex items-center justify-center gap-2 cursor-pointer"
                style={{ backgroundColor: "#06C755" }}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-chat-1-line"></i>
                </div>
                點擊聯繫團長
              </a>

              <button
                type="button"
                onClick={() => {
                  setSubmitted(false);
                  setSelectedMethod("");
                  setAmount("");
                  setErrorMsg("");
                }}
                className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-all cursor-pointer"
              >
                再儲值一筆
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ============ TRIAL BONUS (independent logic) ============
  const handleClaimTrial = async () => {
    if (!user || trialClaimed || claimingTrial) return;
    setClaimingTrial(true);
    setTrialError("");
    try {
      await adjustBalance(user.id, 1000, '體驗金 1000 元發放');
      setTrialClaimed(true);
      setShowTrialSuccess(true);
      refreshUser();
    } catch (err) {
      console.error('[ClaimTrial] 失敗:', err);
      setTrialError(err instanceof Error ? err.message : '領取體驗金失敗，請稍後再試');
    } finally {
      setClaimingTrial(false);
    }
  };

  // ============ FORM STATE ============
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setApiError("");

    if (!selectedMethod) {
      setErrorMsg("請選擇支付方式");
      return;
    }

    const validation = validateMoneyInput(amount);
    if (!validation.ok) {
      setErrorMsg(validation.error);
      return;
    }

    setIsSubmitting(true);

    // Always open LINE first — user must never be blocked from contacting leader
    const lineWindow = window.open(lineUrl, "_blank", "noopener,noreferrer");

    try {
      await createRechargeRequest({
        user_id: user.id,
        user_name: user.name || user.email || "未命名",
        user_email: user.email,
        amount: validation.value,
        transfer_code: "pending",
        voucher_url: null,
      });
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : "後台同步延遲";
      console.error("[Recharge] createRechargeRequest failed:", err);
      setApiError(errMsg);
      // Don't block the user — still show success so they can contact leader via LINE
    }

    // Always show success state regardless of API result
    setSubmittedAmount(validation.value);
    setSubmitted(true);
    setIsSubmitting(false);
  };

  const activeStep = selectedMethod ? (amount ? 2 : 1) : 0;

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <div className="pt-20 pb-12">
        <div className="px-6 lg:px-12 max-w-2xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-6 text-sm text-gray-500">
            <button onClick={() => navigate("/dashboard")} className="hover:text-amber-600 cursor-pointer">
              個人中心
            </button>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </div>
            <span className="text-gray-700">儲值中心</span>
          </div>

          {/* Page Title */}
          <div className="text-center mb-8">
            <h1 className="text-xl font-bold text-gray-900 mb-1">儲值申請</h1>
            <p className="text-sm text-gray-500">選擇支付方式並輸入金額，提交後聯繫團長完成付款</p>
          </div>

          {/* Trial Bonus Claim Block */}
          <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-5 mb-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0 text-emerald-600">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-gift-line text-lg"></i>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-gray-900 mb-1">新會員體驗金</h3>
                <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                  首次註冊即可領取 1000 點體驗金，立即開始產品認購體驗！
                </p>
                {trialClaimed ? (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-medium">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-check-line text-xs"></i>
                    </div>
                    已領取體驗金 1000 點
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleClaimTrial}
                    disabled={claimingTrial}
                    className="px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2"
                  >
                    {claimingTrial ? (
                      <>
                        <div className="w-4 h-4 flex items-center justify-center animate-spin">
                          <i className="ri-loader-2-line text-xs"></i>
                        </div>
                        發放中...
                      </>
                    ) : (
                      <>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-gift-line text-xs"></i>
                        </div>
                        一鍵領取體驗點數 (限領一次)
                      </>
                    )}
                  </button>
                )}
                {trialError && (
                  <p className="text-xs text-red-500 mt-2">{trialError}</p>
                )}
              </div>
            </div>
          </div>

          {/* Step Guide */}
          <div className="bg-white rounded-xl border border-gray-100 p-5 mb-5">
            <div className="grid grid-cols-2 gap-y-5 gap-x-3 md:flex md:items-start md:gap-0">
              {steps.map((step, idx) => {
                const isActive = activeStep >= idx;
                const isCurrent = activeStep === idx;
                const isLast = idx === steps.length - 1;
                return (
                  <div key={step.num} className="flex items-start md:flex-1">
                    <div className="flex flex-col items-center flex-1 min-w-0">
                      <div
                        className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all shrink-0 ${
                          isCurrent
                            ? "bg-amber-500 text-white ring-4 ring-amber-100"
                            : isActive
                            ? "bg-amber-500 text-white"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${step.icon} text-[10px] md:text-xs`}></i>
                        </div>
                      </div>
                      <span
                        className={`text-[11px] md:text-xs mt-2 font-semibold text-center leading-tight ${
                          isActive ? "text-gray-800" : "text-gray-500"
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                    {!isLast && (
                      <div
                        className={`hidden md:block h-0.5 flex-1 mt-[18px] mx-1 sm:mx-2 transition-all ${
                          isActive ? "bg-amber-300" : "bg-gray-100"
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Step 1: Payment Methods */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center">
                  1
                </span>
                <h2 className="text-sm font-semibold text-gray-900">選擇支付方式</h2>
                <span className="text-red-400 text-xs">*</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setSelectedMethod(method.id)}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
                      selectedMethod === method.id
                        ? "border-amber-400 bg-amber-50/50"
                        : "border-gray-100 hover:border-gray-200 bg-white"
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center shrink-0 ${method.iconColor}`}
                    >
                      <i className={`${method.icon} text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{method.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{method.description}</p>
                    </div>
                    {selectedMethod === method.id && (
                      <div className="ml-auto w-5 h-5 flex items-center justify-center text-amber-500">
                        <i className="ri-checkbox-circle-fill text-lg"></i>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Amount selection */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center">
                  2
                </span>
                <h2 className="text-sm font-semibold text-gray-900">輸入儲值金額</h2>
                <span className="text-red-400 text-xs">*</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {presetAmounts.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setAmount(String(a))}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                      amount === String(a)
                        ? "bg-amber-500 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    ${a}
                  </button>
                ))}
              </div>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="或輸入自訂金額"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
              />
            </div>

            {/* Error message */}
            {errorMsg && (
              <div className="rounded-lg px-3 py-2.5 text-sm border bg-red-50 border-red-200 text-red-600 flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-xs"></i>
                </div>
                {errorMsg}
              </div>
            )}

            {/* Step 3: Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 rounded-xl bg-amber-500 text-white font-medium text-sm hover:bg-amber-600 transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center animate-spin">
                    <i className="ri-loader-2-line text-sm"></i>
                  </div>
                  提交中...
                </span>
              ) : (
                "提交儲值申請"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Trial Bonus Success Modal */}
      {showTrialSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center space-y-4 shadow-xl">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto text-emerald-500">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-check-double-line text-2xl"></i>
              </div>
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-1">領取成功</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                體驗金 1000 元已發放至您的帳戶，請開始進行產品認購體驗。
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowTrialSuccess(false)}
              className="w-full py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 transition-all cursor-pointer"
            >
              我知道了
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}